import axios, { AxiosRequestConfig } from 'axios'

/** 兜底 pmapi，避免 baseURL 为空时请求落到前端静态站（返回 index.html） */
const FALLBACK_PMAPI = 'https://test-pmapi.klicklpay.com/'

const getUrl = function () {
  const key = process.env.VUE_APP_BUILD_ENV
  const type = process.env.VUE_APP_TYPE
  // 构建期注入的绝对地址优先（与 vue.config resolvePmapiBase 一致）
  const injected = (process.env.VUE_APP_PMAPI_BASE || '').trim()
  if (injected && /^https?:\/\//i.test(injected)) {
    return injected.endsWith('/') ? injected : `${injected}/`
  }
  if (key == 'dev') {
    return 'https://test-pmapi.klicklpay.com/'
  }
  if (key == 'pretest') {
    return 'https://test-pmapi.klicklpay.com/'
  }
  if (key == 'pre') {
    return 'https://prepmapi.klicklpay.com/'
  }
  if (key == 'uat') {
    return 'https://uat-pmapi.klicklpay.com/'
  }
  if (key == 'sandbox') {
    return 'https://sandbox-pmapi.klicklpay.com/'
  }
  if (key == 'sunpaysandbox') {
    return 'https://sandbox-pmapi.sunpay.pro/'
  }
  if (key == 'sunpayprod') {
    if (type == 'hk') {
      return 'https://pmapi.sunpayhk.com/'
    }
    return 'https://fiat-pmapi.sunpay.pro/'
  }
  if (key == 'prod') {
    return 'https://pmapi.klicklpay.com/'
  }
  console.warn('[api] unknown VUE_APP_BUILD_ENV, fallback pmapi', key)
  return FALLBACK_PMAPI
}

/** 真实后端域名（构建产物 / WebSocket 等仍用绝对地址） */
const baseURL = getUrl() || FALLBACK_PMAPI

/**
 * 仅本地 `vue-cli-service serve` 为 1（vue.config 写入）。
 * 发版构建恒为 0，必须走绝对 pmapi，禁止出现 test-pay 域名 + /api。
 */
const isDevProxy = process.env.VUE_APP_API_USE_PROXY === '1'
const requestBaseURL = isDevProxy ? '/' : baseURL

console.log('[api] request routing', {
  env: process.env.VUE_APP_BUILD_ENV,
  useProxy: process.env.VUE_APP_API_USE_PROXY,
  baseURL,
  requestBaseURL
})

/**
 * 拼接绝对 API URL（各环境 pmapi 全路径，供 WS、外链等使用）。
 */
const toAbsoluteUrl = (pathOrUrl: string) => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl
  }
  const base = String(baseURL || FALLBACK_PMAPI).replace(/\/?$/, '/')
  const path = String(pathOrUrl || '').replace(/^\//, '')
  try {
    return new URL(path, base).toString()
  } catch (e) {
    return base + path
  }
}

/**
 * 实际发 HTTP 请求用的 URL：本地相对路径走 devServer 代理，其它环境拼绝对路径。
 */
const toRequestUrl = (pathOrUrl: string) => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl
  }
  const path = String(pathOrUrl || '').startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  if (isDevProxy) {
    return path
  }
  return toAbsoluteUrl(path)
}

/**
 * 首屏轻量 GET：credentials omit 的 CORS fetch（带超时，避免挂起拖慢首屏）。
 */
const fetchJsonOmitCredentials = async (pathOrUrl: string, timeoutMs = 12000) => {
  const url = toRequestUrl(pathOrUrl)
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer: any = null
  if (controller) {
    timer = setTimeout(() => {
      try {
        controller.abort()
      } catch (e) { /* ignore */ }
    }, timeoutMs)
  }
  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      },
      signal: controller ? controller.signal : undefined
    })
    if (!res.ok) {
      const err: any = new Error('HTTP ' + res.status)
      err.response = { status: res.status }
      throw err
    }
    const contentType = String(res.headers.get('content-type') || '')
    if (contentType.includes('text/html')) {
      throw new Error('API returned HTML instead of JSON: ' + url)
    }
    return await res.json()
  } catch (e: any) {
    if (e && (e.name === 'AbortError' || e.code === 20)) {
      const err: any = new Error('Network Error')
      throw err
    }
    throw e
  } finally {
    if (timer) {
      clearTimeout(timer)
    }
  }
}

const request = async (axiosConfig: AxiosRequestConfig) => {
  const resolvedBase = isDevProxy ? '/' : (baseURL || FALLBACK_PMAPI)
  const instance = axios.create({
    timeout: 30000,
    withCredentials: false
  })

  instance.defaults.baseURL = resolvedBase
  instance.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8'
  instance.interceptors.request.use(
    config => {
      return config
    },
    async error => {
      return await Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    response => {
      const data = response?.data
      if (typeof data === 'string' && data.indexOf('without JavaScript enabled') !== -1) {
        return Promise.reject(new Error('API hit frontend HTML, check baseURL: ' + resolvedBase))
      }
      return response
    },
    async error => {
      const { message } = error
      if (message === 'Network Error') {
        return await Promise.reject(error)
      } else if (message && message.includes('timeout')) {
        return await Promise.reject(error)
      }
      return await Promise.reject(error)
    }
  )
  return await instance.request(axiosConfig)
}

export {
  baseURL,
  requestBaseURL,
  request,
  fetchJsonOmitCredentials,
  toAbsoluteUrl,
  toRequestUrl
}
